import React from 'react';
import { X, FileText, User, TrendingUp, Calendar } from 'lucide-react';

/**
 * Modal para mostrar las condiciones de una oferta específica
 * Usado en el panel de administración
 */
const BidConditionsModal = ({ bid, onClose }) => {
  if (!bid) return null;

  const hasConditions = bid.selectedConditions?.length > 0 || bid.conditionsText?.trim();

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
      onClick={onClose}
    >
      <div 
        className="bg-slate-800 rounded-2xl max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/10 p-2 rounded-lg">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Detalles de la Oferta</h3>
              <p className="text-sm text-slate-400">Información completa con condiciones</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-6">
          {/* Información de la oferta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Comprador */}
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-400">Comprador</span>
              </div>
              <div className="text-white font-semibold">{bid.buyerName}</div>
              <div className="text-sm text-slate-400">{bid.buyerEmail}</div>
            </div>

            {/* Monto */}
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-amber-400">Monto ofertado</span>
              </div>
              <div className="text-3xl font-bold text-amber-300">
                {bid.amount.toLocaleString()}€
              </div>
            </div>

            {/* Fecha */}
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-400">Fecha de oferta</span>
              </div>
              <div className="text-white font-semibold">
                {new Date(bid.timestamp).toLocaleDateString()}
              </div>
              <div className="text-sm text-slate-400">
                {new Date(bid.timestamp).toLocaleTimeString()}
              </div>
            </div>

            {/* Estado de condiciones */}
            <div className={`rounded-lg p-4 border ${
              hasConditions 
                ? 'bg-blue-500/10 border-blue-500/20' 
                : 'bg-slate-900/50 border-slate-700'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <FileText className={`w-4 h-4 ${hasConditions ? 'text-blue-400' : 'text-slate-400'}`} />
                <span className={`text-xs ${hasConditions ? 'text-blue-400' : 'text-slate-400'}`}>
                  Condiciones
                </span>
              </div>
              <div className={`font-semibold ${hasConditions ? 'text-blue-300' : 'text-slate-500'}`}>
                {hasConditions ? 'Con condiciones' : 'Sin condiciones'}
              </div>
            </div>
          </div>

          {/* Condiciones */}
          {hasConditions ? (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-blue-400" />
                <h4 className="text-lg font-semibold text-blue-400">Condiciones de la oferta</h4>
              </div>

              {/* Condiciones predefinidas */}
              {bid.selectedConditions && bid.selectedConditions.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm text-slate-400 mb-3">Condiciones seleccionadas:</div>
                  <div className="space-y-2">
                    {bid.selectedConditions.map((condition, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-3 bg-slate-900/50 rounded-lg p-3 border border-slate-700"
                      >
                        <span className="text-2xl">{condition.icon}</span>
                        <span className="text-slate-200 flex-1">{condition.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Texto libre */}
              {bid.conditionsText && bid.conditionsText.trim() && (
                <div>
                  <div className="text-sm text-slate-400 mb-2">Condiciones adicionales:</div>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-slate-200 whitespace-pre-wrap">{bid.conditionsText}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">Esta oferta no tiene condiciones adjuntas</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 bg-slate-800/50">
          <button
            onClick={onClose}
            className="w-full bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default BidConditionsModal;